import type { OppSource } from '@crm/shared';
import { useEffect, useState } from 'react';

import type {
  Custodian,
  Opportunity,
  OpportunityFull,
  OpportunityInlineEditPayload,
} from '../../lib/api';
import { apiClient } from '../../lib/api';

type Props = {
  open: boolean;
  opportunity: Opportunity | OpportunityFull;
  onClose: () => void;
  onSaved: (updated: Opportunity) => void;
};

const SOURCES: { value: OppSource; label: string }[] = [
  { value: 'recommendation', label: 'Referral' },
  { value: 'event', label: 'Event' },
  { value: 'intermediary', label: 'Intermediary' },
  { value: 'bank', label: 'Bank' },
  { value: 'lawyer', label: 'Lawyer / Notary' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'other', label: 'Other' },
];

/**
 * Full-form edit modal for an existing opportunity. Wires into the same
 * PATCH /opportunities/:id endpoint used by the table inline editor, but
 * exposes every editable column at once for the detail page workflow.
 *
 * Stage changes still route through /:id/stage so the KYC compliance gate
 * (and audit trail) keep running.
 */
export function OpportunityEditModal({ open, opportunity, onClose, onSaved }: Props) {
  const [form, setForm] = useState<OpportunityInlineEditPayload>(() => initial(opportunity));
  const [custodians, setCustodians] = useState<Custodian[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm(initial(opportunity));
    setError(null);
    setSubmitting(false);
    // Custodians are tiny — fine to refetch on every open in dev. Use the
    // existing client cache later if needed.
    apiClient
      .listCustodians()
      .then(setCustodians)
      .catch(() => undefined);
  }, [open, opportunity]);

  if (!open) return null;

  function set<K extends keyof OpportunityInlineEditPayload>(
    key: K,
    value: OpportunityInlineEditPayload[K],
  ) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name?.trim()) {
      setError('Name is required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const updated = await apiClient.patchOpportunity(opportunity.id, form);
      onSaved(updated);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Edit opportunity"
      className="fixed inset-0 z-40 bg-graphite/40 backdrop-blur-[2px] flex items-start lg:items-center justify-center overflow-y-auto"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-3xl bg-surface rounded-card shadow-panel m-4 lg:m-10"
      >
        <header className="px-6 py-4 border-b border-platinum">
          <p className="label-uppercase">— Edit opportunity</p>
          <h2 className="mt-1 font-display text-xl font-semibold">{opportunity.name}</h2>
        </header>

        <div className="px-6 py-5 space-y-6">
          <Section title="Identification">
            <Field label="Name *" full>
              <input
                required
                value={form.name ?? ''}
                onChange={(e) => set('name', e.target.value)}
                className="input-base"
              />
            </Field>
            <Field label="Passport / Origin">
              <input
                value={form.passport_origin ?? ''}
                onChange={(e) => set('passport_origin', e.target.value || null)}
                className="input-base"
              />
            </Field>
            <Field label="Domiciled country">
              <input
                value={form.domiciled_country ?? ''}
                onChange={(e) => set('domiciled_country', e.target.value)}
                className="input-base"
              />
            </Field>
            <Field label="Industry / Sector">
              <input
                value={form.industry_sector ?? ''}
                onChange={(e) => set('industry_sector', e.target.value)}
                className="input-base"
              />
            </Field>
          </Section>

          <Section title="Amounts">
            <Field label="Total Wealth (CHF)">
              <input
                type="number"
                step="100000"
                value={form.total_wealth ?? 0}
                onChange={(e) => set('total_wealth', Number(e.target.value))}
                className="input-base font-tabular text-right"
              />
            </Field>
            <Field label="Initial Expected (CHF)">
              <input
                type="number"
                step="100000"
                value={form.initial_expected_amount ?? 0}
                onChange={(e) => set('initial_expected_amount', Number(e.target.value))}
                className="input-base font-tabular text-right"
              />
            </Field>
            <Field label="RoA (bps)">
              <input
                type="number"
                step="1"
                value={form.roa_bps ?? ''}
                onChange={(e) =>
                  set('roa_bps', e.target.value === '' ? null : Number(e.target.value))
                }
                className="input-base font-tabular text-right"
              />
            </Field>
            <Field label="Potential Y+1 (CHF)">
              <input
                type="number"
                step="100000"
                disabled={form.potential_negotiation === true}
                value={form.potential_expected_amount_y1 ?? 0}
                onChange={(e) => set('potential_expected_amount_y1', Number(e.target.value))}
                className="input-base font-tabular text-right disabled:opacity-50"
              />
            </Field>
            <Field label='"Negotiation" mode'>
              <label className="flex items-center gap-2 mt-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.potential_negotiation === true}
                  onChange={(e) => set('potential_negotiation', e.target.checked)}
                />
                Mark as in negotiation
              </label>
            </Field>
            <Field label="Expected timeframe">
              <input
                value={form.expected_timeframe ?? ''}
                onChange={(e) => set('expected_timeframe', e.target.value || null)}
                placeholder="e.g. 3 months"
                className="input-base"
              />
            </Field>
          </Section>

          <Section title="Banks">
            <Field label="Existing bank">
              <input
                value={form.existing_bank ?? ''}
                onChange={(e) => set('existing_bank', e.target.value || null)}
                className="input-base"
              />
            </Field>
            <Field label="Target bank">
              <select
                value={form.target_bank_id ?? ''}
                onChange={(e) => set('target_bank_id', e.target.value || null)}
                className="select-base"
              >
                <option value="">—</option>
                {custodians.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.code} · {c.name}
                  </option>
                ))}
              </select>
            </Field>
          </Section>

          <Section title="Source &amp; relation">
            <Field label="Source">
              <select
                value={form.source ?? 'other'}
                onChange={(e) => set('source', e.target.value as OppSource)}
                className="select-base"
              >
                {SOURCES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Referred by">
              <input
                value={form.referred_by ?? ''}
                onChange={(e) => set('referred_by', e.target.value || null)}
                className="input-base"
              />
            </Field>
          </Section>

          <Section title="Compliance">
            <Field label="KYC status">
              <select
                value={form.kyc_status ?? 'pending'}
                onChange={(e) =>
                  set('kyc_status', e.target.value as OpportunityInlineEditPayload['kyc_status'])
                }
                className="select-base"
              >
                <option value="pending">Pending</option>
                <option value="in_progress">In progress</option>
                <option value="complete">Complete</option>
                <option value="expired">Expired</option>
              </select>
            </Field>
            <Field label="KYC last review">
              <input
                type="date"
                value={form.kyc_last_review_date ?? ''}
                onChange={(e) => set('kyc_last_review_date', e.target.value || null)}
                className="input-base font-tabular"
              />
            </Field>
            <Field label="PEP?">
              <label className="flex items-center gap-2 mt-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.pep_flag === true}
                  onChange={(e) => set('pep_flag', e.target.checked)}
                />
                Politically Exposed Person
              </label>
            </Field>
            <Field label="Reverse solicitation evidence" full>
              <textarea
                rows={2}
                value={form.reverse_solicitation_evidence ?? ''}
                onChange={(e) => set('reverse_solicitation_evidence', e.target.value || null)}
                className="input-base"
                placeholder="Email / letter reference / date of inbound contact"
              />
            </Field>
          </Section>

          <p className="text-xs text-ink-tertiary leading-relaxed">
            Stage is not editable here: it transitions through the Kanban (drag &amp; drop) or the
            detail page, to preserve the audit trail and the KYC gate (FINMA + SEC).
          </p>

          {error && (
            <p className="text-sm text-semantic-error border border-signal/30 bg-bg-alert px-3 py-2 rounded-card">
              {error}
            </p>
          )}
        </div>

        <footer className="flex items-center justify-end gap-3 px-6 py-4 border-t border-platinum">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-60">
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </footer>
      </form>
    </div>
  );
}

function initial(o: Opportunity | OpportunityFull): OpportunityInlineEditPayload {
  return {
    name: o.name,
    passport_origin: o.passport_origin,
    domiciled_country: o.domiciled_country,
    industry_sector: o.industry_sector,
    total_wealth: o.total_wealth,
    initial_expected_amount: o.initial_expected_amount,
    roa_bps: o.roa_bps,
    potential_expected_amount_y1: o.potential_expected_amount_y1,
    potential_negotiation: o.potential_negotiation,
    expected_timeframe: o.expected_timeframe,
    existing_bank: o.existing_bank,
    target_bank_id: o.target_bank?.id ?? null,
    source: o.source as OppSource,
    referred_by: 'referred_by' in o ? (o.referred_by ?? null) : null,
    pep_flag: o.pep_flag,
    kyc_status: o.kyc_status,
    kyc_last_review_date: o.kyc_last_review_date,
    reverse_solicitation_evidence: o.reverse_solicitation_evidence,
  };
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset>
      <legend className="label-uppercase mb-3">{title}</legend>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">{children}</div>
    </fieldset>
  );
}

function Field({
  label,
  children,
  full,
}: {
  label: string;
  children: React.ReactNode;
  full?: boolean;
}) {
  return (
    <label className={'block text-sm ' + (full ? 'md:col-span-2' : '')}>
      <span className="block text-xs uppercase tracking-wider text-ink-secondary mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
